import * as React from 'react';
import { cn } from '../../../utils/ClassNamesUtils';
import { OSCAR_MARK_SIZE } from '../../constants/AIAgentConstants';
import OscarMark from './OscarMark';

interface OscarThinkingIndicatorProps {
	/** What is happening, derived from the stream. */
	label: string;
	/** Whole seconds since the turn began. */
	seconds: number;
	className?: string;
}

/**
 * Under a second reads as noise, so the timer only appears once there is one.
 * Past a minute it splits, because "94s" makes the reader do the arithmetic.
 */
export const formatElapsed = (seconds: number): string => {
	if (seconds < 60) {
		return `${seconds}s`;
	}
	const minutes = Math.floor(seconds / 60);
	const remainder = seconds % 60;
	if (minutes < 60) {
		return `${minutes}m ${remainder}s`;
	}
	return `${Math.floor(minutes / 60)}h ${minutes % 60}m ${remainder}s`;
};

/**
 * The "something is happening" row: pulsing mark, elapsed time, then what the
 * agent is doing.
 *
 * It appears the moment a message is sent — not when the first stream event
 * lands — because the gap in between is exactly where the panel used to look
 * broken. Oscar rides his own wingbeat, the timer counts so a long turn reads as
 * slow rather than stuck, and the label reports whatever the stream last said.
 *
 * The timer sits between the two so the eye reads "how long" before "at what".
 * It takes only the width it needs — a reserved slot kept the activity line
 * perfectly still but left an obvious empty gap, which is worse to look at than
 * the odd nudge when the count gains a digit.
 */
export const OscarThinkingIndicator = ({
	label,
	seconds,
	className,
}: OscarThinkingIndicatorProps) => (
	<div
		className={cn('flex w-full shrink-0 items-center gap-s', className)}
		role="status"
		aria-live="polite"
	>
		<OscarMark size={OSCAR_MARK_SIZE.loading} animation="flap" />
		{seconds > 0 ? (
			<span className="footnoteregular shrink-0 tabular-nums text-character-secondary">
				{formatElapsed(seconds)}
			</span>
		) : null}
		<span className="bodyregular min-w-0 flex-1 truncate text-character-secondary">{label}</span>
	</div>
);

export default OscarThinkingIndicator;
